import { useQuery } from "@tanstack/react-query";
import { Badge, cn } from "@tcg/ui";
import { ArrowLeft, Banknote, CreditCard, Gift, Loader2, Split, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router";

import { ApprovalCancelled } from "@/app/approval";
import { usePos, useRegister } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText, useYen } from "@/components/MoneyText";
import { useToast } from "@/components/Toast";
import { CustomerModal } from "@/features/sale/CustomerModal";
import { useCart } from "@/features/sale/hooks/useCart";
import { call } from "@/lib/api";
import { errorText, isApiError } from "@/lib/errors";
import type { Cart, Sale, SaleCreate } from "@/lib/types";

import { CashPad } from "./CashPad";
import { EdcForm } from "./EdcForm";
import { ReceiptOptions } from "./ReceiptOptions";
import { SplitList } from "./SplitList";
import { StoreCreditForm } from "./StoreCreditForm";
import { useTender, type TenderMode } from "./useTender";

const MODES: Array<{ mode: TenderMode; label: string; icon: typeof Banknote }> = [
  { mode: "cash", label: "tender.method_cash", icon: Banknote },
  { mode: "card", label: "tender.method_card", icon: CreditCard },
  { mode: "credit", label: "tender.method_credit", icon: Gift },
  { mode: "split", label: "tender.method_split", icon: Split },
];

function modeOf(value: string | null): TenderMode {
  if (value === "card") {
    return "card";
  }
  if (value === "other") {
    return "split";
  }
  return "cash";
}

export interface DoneState {
  sale: Sale;
  qr: boolean;
  /** 免税 (T07-30): the buyer's language — the receipt / 案内 print in it. */
  locale?: SaleCreate["locale"];
}

/** What the 免税 wizard hands to お会計 (T07-30). */
export interface TenderEntryState {
  taxFree?: { locale: NonNullable<SaleCreate["locale"]>; email: string };
}

/** お会計 (design/05 §3.2, T07-24) — the screen opens from the sale screen's pay buttons. */
export function TenderScreen() {
  const cart = useCart();
  const [params] = useSearchParams();
  if (!cart.cart) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 aria-hidden className="size-10 animate-spin" />
      </div>
    );
  }
  if (!cart.canPay && cart.cart.status !== "paying") {
    return <Navigate to="/" replace />;
  }
  return <Tender cart={cart.cart} initialMode={modeOf(params.get("method"))} />;
}

function Tender({ cart, initialMode }: { cart: Cart; initialMode: TenderMode }) {
  const { t } = useTranslation();
  const yen = useYen();
  const { api, printer } = usePos();
  const register = useRegister();
  const cartApi = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const entry = (useLocation().state as TenderEntryState | null)?.taxFree;
  const [customerOpen, setCustomerOpen] = useState(false);
  const [voidCheck, setVoidCheck] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const methods = useQuery({
    queryKey: ["payment-methods", "list", "pos"],
    queryFn: () => call(() => api.GET("/v1/pos/payment-methods")),
    staleTime: 60_000,
  });
  const rules = register.data?.rules;
  const tender = useTender({
    cart,
    methods: methods.data,
    initialMode,
    defaultDocumentType: rules?.defaultDocumentType ?? "kani_invoice",
    defaultPrint: (rules?.defaultReceiptMode ?? "print").includes("print"),
    paperWidth: printer.config.paperWidth,
    ...(entry ? { customerLocale: entry.locale, initialEmail: entry.email } : {}),
  });
  const stripe = register.data?.features.stripeTerminal === true;
  const customer = cart.customer ?? null;
  const preview = tender.preview;

  const receive = async () => {
    if (!tender.canSubmit) {
      return;
    }
    setNotice(null);
    try {
      const sale = await tender.submit();
      const state: DoneState = {
        sale,
        qr: tender.receipt.qr,
        ...(entry ? { locale: entry.locale } : {}),
      };
      await cartApi.startNew(); // the basket is sold — the next customer gets a new one
      void navigate(`/done/${sale.sale.id}`, { state, replace: true });
    } catch (error) {
      if (error instanceof ApprovalCancelled) {
        return; // the manager did not confirm — nothing was charged, the cashier picks again
      }
      if (isApiError(error, "hold_lost")) {
        cartApi.refresh();
        if (error.detail.edcVoidRequired === true) {
          setVoidCheck(true);
          return;
        }
        toast.show(t("tender.hold_lost"), "error");
        void navigate("/", { replace: true });
      } else if (isApiError(error, "tax_unavailable")) {
        setNotice(t("tender.tax_unavailable"));
      } else if (
        isApiError(error, "sale_payment_unconfirmed") ||
        isApiError(error, "sale_in_progress") ||
        isApiError(error, "network")
      ) {
        setNotice(t("tender.unconfirmed"));
      } else if (
        isApiError(error, "payment_total_mismatch") ||
        isApiError(error, "version_conflict") ||
        isApiError(error, "cart_not_open")
      ) {
        cartApi.refresh();
        setNotice(errorText(error, t));
      } else {
        setNotice(errorText(error, t));
      }
    }
  };

  const firstPart = tender.parts[0];
  const cardMode = tender.mode === "card";
  const receiveLabel = cardMode
    ? t("tender.card_confirm")
    : t("tender.receive", { amount: yen(tender.total) });
  const shortBy = preview?.tenders.reduce<number | null>(
    (found, line) => found ?? (line.shortBy && line.shortBy > 0 ? line.shortBy : null),
    null,
  );
  const remaining = preview && preview.remaining > 0 ? preview.remaining : (shortBy ?? 0);

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_1fr]">
      <header className="flex items-center gap-4 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => void navigate("/")}
          disabled={tender.submitting}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-4 text-lg disabled:opacity-50"
        >
          <ArrowLeft aria-hidden />
          {t("tender.back_to_sale")}
        </button>
        {cart.taxFree ? (
          <p
            data-testid="tender-tax-free"
            className="rounded-md border border-status-pending px-3 py-2"
          >
            <Badge variant="pending">{t("taxfree.badge")}</Badge>{" "}
            {cart.taxFree.regime === "refund"
              ? t("taxfree.tender_refund", { amount: yen(cart.taxFree.refundExpected ?? 0) })
              : t("taxfree.tender_exemption")}
          </p>
        ) : null}
        <h1 className="flex flex-1 items-baseline justify-end gap-3 text-2xl font-semibold">
          <span>{t("tender.title")}</span>
          <MoneyText amount={tender.total} size="hero" data-testid="tender-total" />
        </h1>
      </header>
      <nav aria-label={t("tender.title")} className="flex gap-2 border-b px-4 py-2">
        {MODES.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            aria-pressed={tender.mode === mode}
            onClick={() => tender.setMode(mode)}
            disabled={tender.submitting}
            className={cn(
              "flex min-h-pos-touch flex-1 items-center justify-center gap-2 rounded-md border text-lg font-semibold [&_svg]:size-6",
              tender.mode === mode && "border-primary bg-primary text-primary-foreground",
            )}
          >
            <Icon aria-hidden />
            {t(label)}
          </button>
        ))}
        {stripe ? (
          <button
            type="button"
            disabled
            className="flex min-h-pos-touch flex-1 items-center justify-center gap-2 rounded-md border text-lg opacity-60"
          >
            {t("tender.stripe")} <Badge variant="outline">{t("tender.stripe_coming")}</Badge>
          </button>
        ) : null}
      </nav>
      <div className="grid min-h-0 grid-cols-[minmax(0,1fr)_22rem] gap-4 overflow-hidden p-4 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <section
          aria-label={t(MODES.find((m) => m.mode === tender.mode)?.label ?? "tender.title")}
          className="min-h-0 overflow-y-auto"
        >
          {tender.mode === "cash" && firstPart ? (
            <CashPad
              part={firstPart}
              index={0}
              preview={preview}
              onTendered={(digits) => tender.update(firstPart.id, { tendered: digits })}
            />
          ) : null}
          {tender.mode === "card" && firstPart ? (
            <EdcForm
              part={firstPart}
              index={0}
              methods={methods.data}
              preview={preview}
              onChange={(c) => tender.update(firstPart.id, c)}
            />
          ) : null}
          {tender.mode === "credit" && firstPart ? (
            <StoreCreditForm
              part={firstPart}
              customer={customer}
              preview={preview}
              onAmount={(digits) => tender.update(firstPart.id, { amount: digits })}
              onChooseCustomer={() => setCustomerOpen(true)}
            />
          ) : null}
          {tender.mode === "split" ? (
            <SplitList
              parts={tender.parts}
              preview={preview}
              methods={methods.data}
              customer={customer}
              onAdd={tender.addPart}
              onChange={tender.update}
              onRemove={tender.removePart}
              onChooseCustomer={() => setCustomerOpen(true)}
            />
          ) : null}
          {tender.mode === "credit" && preview && preview.remaining > 0 ? (
            <button
              type="button"
              onClick={() => tender.setMode("split")}
              className="mt-3 min-h-pos-touch rounded-md border px-4 text-lg"
            >
              {t("tender.split_title")} →
            </button>
          ) : null}
        </section>
        <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          <ReceiptOptions value={tender.receipt} onChange={tender.setReceipt} preview={preview} />
          <div className="mt-auto flex flex-col gap-2">
            {remaining > 0 ? (
              <p
                role="status"
                data-testid="remaining"
                className="flex items-baseline justify-between rounded-md border border-status-problem px-3 py-2 text-pos-problem"
              >
                <span className="text-2xl font-semibold tabular-nums">
                  {t("tender.remaining", { amount: yen(remaining) })}
                </span>
              </p>
            ) : null}
            {notice ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-md border border-status-problem bg-status-problem/10 p-3 text-pos-problem"
              >
                <TriangleAlert aria-hidden className="mt-0.5 size-5 shrink-0" />
                {notice}
              </p>
            ) : null}
            <button
              type="button"
              data-testid="receive"
              onClick={() => void receive()}
              disabled={!tender.canSubmit}
              aria-busy={tender.submitting}
              className="flex min-h-24 flex-col items-center justify-center rounded-lg bg-primary px-4 text-2xl font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              {tender.submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 aria-hidden className="size-6 animate-spin" />
                  {t("tender.sending")}
                </span>
              ) : (
                receiveLabel
              )}
            </button>
          </div>
        </aside>
      </div>
      <CustomerModal
        open={customerOpen}
        current={customer}
        onClose={() => setCustomerOpen(false)}
        onAttach={cartApi.attachCustomer}
        onDetach={cartApi.detachCustomer}
      />
      <Modal
        open={voidCheck}
        title={t("tender.void_required_title")}
        onClose={() => setVoidCheck(false)}
      >
        <p className="whitespace-pre-line text-lg">{t("tender.void_required_steps")}</p>
        <button
          type="button"
          onClick={() => {
            setVoidCheck(false);
            void navigate("/", { replace: true });
          }}
          className="mt-4 min-h-pos-touch w-full rounded-md bg-primary text-lg font-semibold text-primary-foreground"
        >
          {t("tender.void_confirmed")}
        </button>
      </Modal>
    </div>
  );
}
